import { useEffect, useState, useRef } from "react"

// helpers
import Api from "@/_helpers/Api"
import Skeleton from "react-loading-skeleton"

// providers
import { trans } from "@/_providers/Translation"
import { Tooltip, Button } from "@mui/material"

import alertImage from "@/assets/img/contractors/icons/alert.svg"
import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"
import ChartInfo from "./ChartInfo"

const INITIAL_FILTERS = { technician_id: "", date_from: null, date_to: null }

export default function TechnicianInventory() {
  const [parts, setParts] = useState([])
  const [technicians, setTechnicians] = useState([])
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [loading, setLoading] = useState(true)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)

    Api.get(
      `analytics/technicians-parts-in-van?technician_id=${filters.technician_id}&date_from=${filters.date_from || ''}&date_to=${filters.date_to || ''}`,
    )
      .then((res) => {
        setParts(res.data.parts_in_van)
        setTechnicians(res.data.technicians)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    return filters.date_from || filters.date_to ? 1 : 0
  }

  return (
    <div className="technician-inventory">
      <div className="head">
        <h3>
          {trans("analytics.inventoryState")}
          <ChartInfo name="techniciansPartsInVan" />
        </h3>
        <div className="right">
          {loading ? (
            <Skeleton height={40} width={140} count={1} />
          ) : (
            <select
              onChange={(e) => handleFilterChange("technician_id", e.target.value)}
              value={filters.technician_id || technicians[0]?.id || ""}
            >
              {technicians.map((tech) => (
                <option key={tech.id} value={tech.id}>
                  {tech.full_name}
                </option>
              ))}
            </select>
          )}
          <Button onClick={(e) => { compactFilterLayoutRef.current.open(e) }}>
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? <i>{getAppliedFiltersCount()}</i> : <></>}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className='row'><div className='col'>
              <DateRange
                startDate={filters.date_from || ''}
                endDate={filters.date_to || ''}
                onStartDateChange={(date) => handleFilterChange('date_from', date)}
                onEndDateChange={(date) => handleFilterChange('date_to', date)}
              />
            </div></div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={100} count={3} />
      ) : parts.length === 0 ? (
        <div
          className="alert warning"
          style={{
            marginBottom: 0,
            width: "100%",
          }}
        >
          {trans("messages.noDataFound")}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>№</th>
              <th>{trans("tables.name")}</th>
              <th>{trans("random.availability")}</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((part) => (
              <tr key={part.product_id}>
                <td>{part.product_id}</td>
                <td>{part.part_name}</td>
                <td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    {part.min_quantity > part.quantity ? (
                      <Tooltip title={trans("messages.quantityAlert")}>
                        <img src={alertImage} alt="" />
                      </Tooltip>
                    ) : (
                      <></>
                    )}
                    {part.quantity}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
